import type { BlogPost, CreateBlogPostInput, UpdateBlogPostInput } from '../types/blog';

async function request<T>(path: string, idToken: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
      ...init?.headers,
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// `id` sempre na query string (`/api/blog/post?id=...`), não num segmento
// dinâmico de path — ver comentário no topo de api/blog/post.ts.
function postUrl(id: string): string {
  return `/api/blog/post?id=${encodeURIComponent(id)}`;
}

export function listBlogPosts(idToken: string): Promise<{ posts: BlogPost[] }> {
  return request('/api/blog/posts', idToken);
}

export function getBlogPost(id: string, idToken: string): Promise<{ post: BlogPost }> {
  return request(postUrl(id), idToken);
}

export function createBlogPost(input: CreateBlogPostInput, idToken: string): Promise<{ post: BlogPost }> {
  return request('/api/blog/posts', idToken, { method: 'POST', body: JSON.stringify(input) });
}

export function updateBlogPost(
  id: string,
  input: UpdateBlogPostInput,
  idToken: string
): Promise<{ post: BlogPost }> {
  return request(postUrl(id), idToken, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export function publishBlogPost(
  id: string,
  input: UpdateBlogPostInput,
  idToken: string
): Promise<{ post: BlogPost }> {
  // PUT com `publish: true` no corpo, não uma rota /publish separada.
  return request(postUrl(id), idToken, {
    method: 'PUT',
    body: JSON.stringify({ ...input, publish: true }),
  });
}

export function deleteBlogPost(id: string, idToken: string): Promise<{ ok: true }> {
  return request(postUrl(id), idToken, { method: 'DELETE' });
}

/** Rebusca nome/foto em "Minha conta" e reescreve a assinatura de um post
 *  já existente — a denormalização normal só acontece 1x, na criação (ver
 *  api/blog/posts/index.ts), então é a única forma de um post publicado
 *  antes de trocar a foto/nome da conta acompanhar a mudança. */
export function refreshBlogPostAutor(id: string, idToken: string): Promise<{ post: BlogPost }> {
  return request(postUrl(id), idToken, { method: 'PUT', body: JSON.stringify({ refreshAutor: true }) });
}
